// Modelo de navegación del AppShell v2 (sidebar) + mapeo de "color de firma"
// por sección. Cada sección tiene un slug que el AppShell usa para fijar la
// variable CSS --sig (ver index.css · tokens --sig-*) en el contenedor raíz,
// tiñendo el item activo y, en pasos posteriores, la zona de contenido.
//
// El icono es un componente de lucide-react (set fijado para todo el campus v2).
// El mapeo ruta→sección vive aquí para que tanto el sidebar como el breadcrumb
// resuelvan la sección activa de forma consistente.
//
// Orden canónico del sidebar (ajustado por Jaime 2026-06):
// Inicio · Cursos · Mi progreso · Mis robots · Mi plan · Tienda · Mercado ·
// Señales (admin) · Comunidad · Panel admin. Mercado y Señales viven DEBAJO de
// Tienda. El "Tutor IA" NO es una sección del sidebar: es el asistente flotante
// (botón inferior derecho); por eso aquí no existe. La ruta /market es
// "Mercado", no "Tutor IA".

import {
  Home,
  GraduationCap,
  BarChart3,
  LineChart,
  Radio,
  Cpu,
  ShieldCheck,
  ShoppingBag,
  Users,
  MessagesSquare,
  MessageSquarePlus,
  BookOpen,
  Wrench,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { PERMISSIONS, hasPermission, type Permission } from './permissions';

/** Feature flag · cross-sell "Robotea Auto". APAGADO por defecto (prod).
 *  Solo cuando `VITE_AUTO_CROSSSELL_ENABLED === 'true'` el item de nav /auto
 *  aparece en el sidebar. Se evalúa una vez a la carga del módulo. */
const AUTO_CROSSSELL = import.meta.env.VITE_AUTO_CROSSSELL_ENABLED === 'true';

/** Slug de sección · coincide con el sufijo de los tokens --sig-<slug>. */
export type SectionSlug =
  | 'inicio'
  | 'cursos'
  | 'progreso'
  | 'mercado'
  | 'senales'
  | 'robots'
  | 'plan'
  | 'tienda'
  | 'auto'
  | 'roboteam'
  | 'comunidad'
  | 'admin';

/** Sub-item de navegación anidada (hoy solo el Panel admin lo usa). Vive
 *  DEBAJO de su item padre en el propio sidebar, indentado. Hereda la sección
 *  (y por tanto el color de firma) del padre. */
export interface SubNavItem {
  /** Ruta destino (react-router). */
  to: string;
  /** Etiqueta visible. */
  label: string;
  /** `true` cuando la ruta activa debe coincidir EXACTAMENTE (no por prefijo).
   *  Lo usa el "Resumen" de admin, cuya ruta `/admin` es prefijo de todas las
   *  demás y si no marcaría siempre como activo. */
  exact?: boolean;
  /** Permiso atómico que REVELA este sub-item. Si se omite, visible para
   *  cualquiera que vea el item padre (hoy todos los sub-items de admin lo
   *  declaran). El gate REAL es server-side; esto solo decide qué se MUESTRA. */
  requiredPermission?: Permission;
  /** Cabecera de grupo bajo la que se agrupa este sub-item en el submenú.
   *  Los sub-items contiguos con el mismo `group` se muestran bajo una pequeña
   *  cabecera (separa visualmente un submenú largo, p. ej. el Panel admin).
   *  Sin `group` → sin cabecera (p. ej. "Resumen" arriba del todo). */
  group?: string;
  /** data-testid para QA / tests. */
  testId: string;
}

/** Contexto de acceso adicional para la visibilidad del nav (más allá de los
 *  permisos de rol): membresías del usuario efectivo. El gate REAL es
 *  server-side; esto solo decide qué se MUESTRA en el sidebar. */
export interface NavAccessContext {
  /** ¿El usuario efectivo tiene membresía RoboTeam activa? (entitlement
   *  roboteam / roboteam-basic / roboteam-pro · viene de /api/me/profile). */
  roboteamMember: boolean;
}

/** Contexto por defecto (sin membresías) · para llamadas legacy/tests. */
export const EMPTY_NAV_ACCESS: NavAccessContext = { roboteamMember: false };

export interface NavItem {
  /** Ruta destino (react-router). */
  to: string;
  /** Etiqueta visible + texto del tooltip en estado plegado. */
  label: string;
  /** Icono lucide-react · set fijado. */
  icon: LucideIcon;
  /** Sección · fija el color de firma --sig del item activo. */
  section: SectionSlug;
  /** Prefijos de ruta adicionales que también marcan este item como activo
   *  (ej. /dashboard/modulo/:slug pertenece a Inicio). */
  match?: string[];
  /** Visibilidad por PERMISOS (+ contexto de acceso). Recibe el array de
   *  permisos del usuario (el que vino del backend en /api/me/profile) y un
   *  contexto adicional (membresías, p. ej. RoboTeam). Si se omite, visible
   *  para todos los autenticados. Reemplaza al antiguo gate por `role==='admin'`. */
  show?: (permissions: readonly string[], ctx: NavAccessContext) => boolean;
  /** Sub-secciones desplegables en el propio sidebar (nav anidado). Si se
   *  define, el item se comporta como cabecera expandible: pulsarlo despliega
   *  sus hijos debajo (indentados); en estado plegado el hijo aparece en un
   *  flyout al hover. Hoy solo el Panel admin lo usa. */
  children?: SubNavItem[];
  /** data-testid para QA / tests. */
  testId: string;
}

/** Grupos del sidebar · un separador entre cada grupo. El orden replica el nav
 *  canónico del campus original. Grupo 1 = aprendizaje · Grupo 2 = trading y
 *  producto · Grupo 3 = comunidad · Grupo 4 = administración (solo admin). */
export const NAV_GROUPS: NavItem[][] = [
  [
    { to: '/dashboard', label: 'Inicio', icon: Home, section: 'inicio', match: ['/dashboard'], testId: 'shell-link-inicio' },
    { to: '/cursos', label: 'Cursos', icon: GraduationCap, section: 'cursos', match: ['/cursos', '/curso', '/dashboard/modulo'], testId: 'shell-link-cursos' },
    { to: '/progreso', label: 'Mi progreso', icon: BarChart3, section: 'progreso', match: ['/progreso', '/certificado'], testId: 'shell-link-progreso' },
  ],
  [
    // Producto y trading. Orden pedido por Jaime: Mis robots · Mi plan · Tienda,
    // y DESPUÉS Mercado · Señales (ambos bajan por debajo de Tienda).
    { to: '/my-robots', label: 'Mis robots', icon: Cpu, section: 'robots', match: ['/my-robots'], testId: 'shell-link-robots' },
    {
      // Robotea Lab · informes de robots (parámetros/PDF), duplicado del Hub
      // dentro del campus. OCULTO para todos salvo Jaime (decisión Jaime
      // 2026-08-29): gate por `robotea_lab.view`, permiso que la matriz RBAC
      // concede solo a `owner` — ni admin lo ve (ver permissions.py). Va
      // JUSTO debajo de "Mis robots" (decisión Jaime 2026-08-30).
      to: '/robotea-lab',
      label: 'Robotea Lab',
      icon: Cpu,
      section: 'robots',
      match: ['/robotea-lab'],
      show: (perms) => hasPermission(perms, PERMISSIONS.roboteaLabView),
      testId: 'shell-link-robotea-lab',
    },
    { to: '/my-plan', label: 'Mi plan', icon: ShieldCheck, section: 'plan', match: ['/my-plan'], testId: 'shell-link-plan' },
    { to: '/tienda-eas', label: 'Tienda', icon: ShoppingBag, section: 'tienda', match: ['/tienda-eas'], testId: 'shell-link-tienda' },
    // Robotea Auto · cross-sell del servicio gestionado. Nace APAGADO tras el
    // feature flag AUTO_CROSSSELL: solo entra en NAV_GROUPS cuando el flag está
    // on (spread condicional). La PÁGINA /auto decide contenido vs. venta.
    ...(AUTO_CROSSSELL
      ? [{ to: '/auto', label: 'Robotea Auto', icon: Zap, section: 'auto' as const, match: ['/auto'], testId: 'shell-link-auto' }]
      : []),
    { to: '/market', label: 'Mercado', icon: LineChart, section: 'mercado', match: ['/market'], testId: 'shell-link-mercado' },
    {
      // Señales · trading signals. Página real admin-only (RequireAdmin) en
      // /signals. Jaime quiere verla en el menú aunque esté gated para el resto.
      to: '/signals',
      label: 'Señales',
      icon: Radio,
      section: 'senales',
      match: ['/signals'],
      // Señales lee marketdata gated server-side por analitica.view (admin-only
      // hoy = owner/admin/soporte). El menú lo muestra a quien tenga el permiso.
      show: (perms) => hasPermission(perms, PERMISSIONS.analiticaView),
      testId: 'shell-link-senales',
    },
  ],
  [
    { to: '/comunidad', label: 'Comunidad', icon: MessagesSquare, section: 'comunidad', match: ['/comunidad'], testId: 'shell-link-comunidad' },
    // RoboTeam · área de miembros (Basic/Pro). Lo ven (a) los STAFF que
    // gestionan/usan el club — gate por `admin.analitica.view`, permiso que
    // tienen owner/admin/soporte/**tutor** (decisión Jaime 2026-06-25) pero NO
    // los alumnos — y (b) los MIEMBROS reales por entitlement RoboTeam
    // (ctx.roboteamMember · viene de /api/me/profile → fix Jaime 2026-07-22:
    // los alumnos con membresía no veían el item). La página /roboteam ya trata
    // a los staff como Pro (preview) y resuelve el tier del miembro por sus
    // enrolled slugs. Va JUSTO debajo de Comunidad.
    {
      to: '/roboteam',
      label: 'RoboTeam',
      icon: Users,
      section: 'roboteam',
      match: ['/roboteam'],
      show: (perms, ctx) =>
        ctx.roboteamMember || hasPermission(perms, PERMISSIONS.analiticaView),
      testId: 'shell-link-roboteam',
    },
    // Campus nuevo (propuesta visual en staging): solo staff, mismo permiso que RoboTeam.
    {
      to: '/miembros',
      label: 'Campus nuevo',
      icon: Zap,
      section: 'roboteam',
      match: ['/miembros'],
      show: (perms) => hasPermission(perms, PERMISSIONS.analiticaView),
      testId: 'shell-link-miembros',
    },
    // Buzón de sugerencias · feedback del campus. Visible para CUALQUIER usuario
    // autenticado (incl. student): el envío no requiere permiso de admin (gate
    // server-side = require_user). Reusa la sección 'comunidad' para no añadir un
    // token --sig nuevo.
    { to: '/buzon', label: 'Buzón de Sugerencias', icon: MessageSquarePlus, section: 'comunidad', match: ['/buzon'], testId: 'shell-link-buzon' },
    // Wiki · guía del campus para alumnos (accesos por plan, cómo completar los
    // cursos, dónde están los robots, qué hace cada página). Va debajo de Buzón
    // (decisión Jaime 2026-06-17). Visible para cualquier usuario autenticado.
    { to: '/guia', label: 'Wiki', icon: BookOpen, section: 'comunidad', match: ['/guia'], testId: 'shell-link-guia' },
  ],
  [
    {
      to: '/admin',
      label: 'Panel admin',
      icon: Wrench,
      section: 'admin',
      match: ['/admin'],
      // El "Panel admin" se muestra si el usuario ve AL MENOS UNO de sus
      // sub-items (cualquier permiso de admin). Se calcula a partir de los
      // requiredPermission de los hijos (ver isNavItemVisible) → no hay que
      // mantener una lista duplicada aquí. (owner/admin lo ven entero; soporte
      // ve un subconjunto; tutor/staff/student no lo ven.)
      // Sub-secciones REALES del panel · espejo de las rutas /admin/* en
      // App.tsx. Orden canónico de gestión: panorámica → personas → datos →
      // contenido → operación. La navegación admin vive AQUÍ.
      // El "Resumen" se gobierna por usuarios.view (lo ve todo el que entra al
      // panel · admin/owner/soporte).
      // Submenú AGRUPADO (Jaime 2026-06-17): eran ~11 enlaces planos. Se agrupan
      // por área bajo cabeceras (Resumen suelto arriba · Usuarios · Contenido ·
      // Datos · Comunidad · Gestión). El AppShell pinta una cabecera cuando
      // cambia `group`. El gate REAL sigue siendo server-side (requiredPermission
      // solo decide qué se muestra).
      children: [
        { to: '/admin', label: 'Resumen', exact: true, requiredPermission: PERMISSIONS.usuariosView, testId: 'shell-admin-resumen' },
        // Datos
        { to: '/admin/analitica', label: 'Analítica', group: 'Datos', requiredPermission: PERMISSIONS.analiticaView, testId: 'shell-admin-analitica' },
        { to: '/admin/contenido', label: 'Contenido', group: 'Datos', requiredPermission: PERMISSIONS.contenidoWrite, testId: 'shell-admin-contenido' },
        // Eventos en vivo · CRUD de sesiones (RoboTeam y abiertas). La ruta
        // /admin/eventos existía desde fase4 pero sin entrada en el submenú
        // (fix Jaime 2026-07-22: "no veo en admin ninguna sección de eventos").
        { to: '/admin/eventos', label: 'Eventos en vivo', group: 'Datos', requiredPermission: PERMISSIONS.contenidoWrite, testId: 'shell-admin-eventos' },
        { to: '/admin/actividad', label: 'Actividad', group: 'Datos', requiredPermission: PERMISSIONS.actividadView, testId: 'shell-admin-actividad' },
        // Usuarios
        { to: '/admin/usuarios/alumnos', label: 'Gestión de alumnos', group: 'Usuarios', requiredPermission: PERMISSIONS.usuariosView, testId: 'shell-admin-usuarios-alumnos' },
        { to: '/admin/usuarios/staff', label: 'Gestión de usuarios (staff)', group: 'Usuarios', requiredPermission: PERMISSIONS.usuariosView, testId: 'shell-admin-usuarios-staff' },
        { to: '/admin/sugerencias', label: 'Sugerencias', group: 'Usuarios', requiredPermission: PERMISSIONS.sugerenciasView, testId: 'shell-admin-sugerencias' },
        { to: '/admin/waitlist', label: 'Lista de espera', group: 'Usuarios', requiredPermission: PERMISSIONS.usuariosView, testId: 'shell-admin-waitlist' },
        // Gestión
        { to: '/admin/licencias', label: 'Licencias', group: 'Gestión', requiredPermission: PERMISSIONS.licenciasView, testId: 'shell-admin-licencias' },
        { to: '/admin/enlaces', label: 'Enlaces', group: 'Gestión', requiredPermission: PERMISSIONS.integraciones, testId: 'shell-admin-enlaces' },
        { to: '/admin/integraciones', label: 'Integraciones', group: 'Gestión', requiredPermission: PERMISSIONS.integraciones, testId: 'shell-admin-integraciones' },
      ],
      testId: 'shell-link-admin',
    },
  ],
];

/** Lista plana · útil para resolver la sección activa. */
export const NAV_ITEMS: NavItem[] = NAV_GROUPS.flat();

/** Etiquetas legibles por sección · para el breadcrumb de la barra superior. */
export const SECTION_LABEL: Record<SectionSlug, string> = {
  inicio: 'Inicio',
  cursos: 'Cursos',
  progreso: 'Mi progreso',
  mercado: 'Mercado',
  senales: 'Señales',
  robots: 'Mis robots',
  plan: 'Mi plan',
  tienda: 'Tienda',
  auto: 'Robotea Auto',
  roboteam: 'RoboTeam',
  comunidad: 'Comunidad',
  admin: 'Panel admin',
};

/**
 * Resuelve qué item de navegación corresponde a una ruta. Devuelve el match
 * más específico (prefijo más largo) para que p.ej. /dashboard/modulo/x marque
 * Cursos y no Inicio. Si nada coincide, devuelve null (sección por defecto).
 */
export function resolveNavItem(pathname: string): NavItem | null {
  let best: NavItem | null = null;
  let bestLen = -1;
  for (const item of NAV_ITEMS) {
    const prefixes = item.match ?? [item.to];
    for (const prefix of prefixes) {
      const hit = pathname === prefix || pathname.startsWith(prefix + '/');
      if (hit && prefix.length > bestLen) {
        best = item;
        bestLen = prefix.length;
      }
    }
  }
  return best;
}

/** ¿Está activo este sub-item para la ruta dada? `exact` exige coincidencia
 *  literal (para `/admin` Resumen, prefijo de todas las demás). El resto
 *  coincide por prefijo (`/admin/usuarios` también marca `/admin/usuarios/:id`). */
export function isSubItemActive(pathname: string, sub: SubNavItem): boolean {
  if (sub.exact) return pathname === sub.to;
  return pathname === sub.to || pathname.startsWith(sub.to + '/');
}

// ── Visibilidad por permisos (F3 RBAC) ───────────────────────────────────────
// El gate REAL es server-side (require_permission). Estas funciones solo deciden
// qué se MUESTRA en el sidebar a partir del array de permisos del usuario.

/** ¿Se muestra este sub-item? Si declara `requiredPermission`, exige tenerlo;
 *  si no, visible (heredado del padre). */
export function isSubItemVisible(sub: SubNavItem, permissions: readonly string[]): boolean {
  if (!sub.requiredPermission) return true;
  return hasPermission(permissions, sub.requiredPermission);
}

/** Sub-items visibles de un item, según los permisos del usuario. */
export function visibleSubItems(item: NavItem, permissions: readonly string[]): SubNavItem[] {
  if (!item.children) return [];
  return item.children.filter((sub) => isSubItemVisible(sub, permissions));
}

/**
 * ¿Se muestra este item del nav top-level?
 * - Item con `show`: lo decide `show(permissions, ctx)` (permisos + contexto de
 *   acceso, p. ej. membresía RoboTeam).
 * - Item con `children` (cabecera expandible, p.ej. "Panel admin"): visible sii
 *   tiene AL MENOS UN sub-item visible para esos permisos.
 * - Resto: visible (todo autenticado).
 */
export function isNavItemVisible(
  item: NavItem,
  permissions: readonly string[],
  ctx: NavAccessContext = EMPTY_NAV_ACCESS,
): boolean {
  if (item.children && item.children.length > 0) {
    return visibleSubItems(item, permissions).length > 0;
  }
  if (item.show) return item.show(permissions, ctx);
  return true;
}
